import { sendSocketMessage, socketMessageStream } from "./socketStream";
import { first, map, timeout } from "rxjs/operators";
import { generateRandomId } from "./session";
import store from "../store";

let instance = null;

class CoPilot {
    constructor() {
        this.siteId = store.state.site.site._id;
    }

    /**
     * @return {CoPilot}
     */

    static getInstance() {
        if (instance === null) {
            instance = new CoPilot();
        }
        return instance;
    }

    async fixGrammar(message) {
        const id = generateRandomId();
        sendSocketMessage("copilot fix-grammar", {
            siteId: this.siteId,
            message,
            id,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async longSentence(message) {
        const id = generateRandomId();
        sendSocketMessage("copilot long-sentence", {
            siteId: this.siteId,
            message,
            id,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async changeTone(message, tone) {
        const id = generateRandomId();
        sendSocketMessage("copilot change-tone", {
            siteId: this.siteId,
            message,
            id,
            tone,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async recommendAnswer(siteId, userId, conversationId) {
        console.log("recommendAnswer", { siteId, userId, conversationId });
        const id = generateRandomId();
        sendSocketMessage("copilot recommend-answer", { siteId, userId, conversationId, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async ticketAiresponse(siteId, userId, ticketMessage, osrPayload) {
        console.log("ticketAiresponse", { siteId, userId, ticketMessage, osrPayload });
        const id = generateRandomId();
        sendSocketMessage("copilot ticket-ai-response", { siteId, userId, ticketMessage, osrPayload, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async summarizeContent(content) {
        const id = generateRandomId();
        sendSocketMessage("copilot summarize-content", {
            siteId: this.siteId,
            content,
            id,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async analyzeSentiment(content) {
        const id = generateRandomId();
        sendSocketMessage("copilot analyze-sentiment", {
            siteId: this.siteId,
            content,
            id,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async messageTranslation(message, languageCode) {
        const id = generateRandomId();
        sendSocketMessage("copilot message-translation", {
            siteId: this.siteId,
            message,
            languageCode,
            id,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async promptToGenerateResponse(siteId, userId, conversationId, message) {
        console.log("promptToGenerateResponse", { siteId, userId, conversationId, message });
        const id = generateRandomId();
        sendSocketMessage("copilot prompt-to-generate-response", { siteId, userId, conversationId, message, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async mailAiresponse(siteId, userId, mailDetails) {
        console.log("mailAiresponse", { siteId, userId, mailDetails });
        const id = generateRandomId();
        sendSocketMessage("copilot mail-ai-response", { siteId, userId, mailDetails, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async mailPromtMessage(siteId, userId, mailDetails, message) {
        console.log("mailPromtMessage", mailDetails, message);
        const id = generateRandomId();
        sendSocketMessage("copilot mail-promt-message", { siteId, userId, mailDetails, message, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async getSessionRecommendations(data) {
        const id = generateRandomId();
        sendSocketMessage("copilot session-recommendation", { ...data, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(10000)
            )
            .toPromise();
    }

    async createWorkspaceAppModules(data) {
        const id = generateRandomId();
        sendSocketMessage("copilot create-workspace", { ...data, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(60000 * 3)
            )
            .toPromise();
    }

    async createTableReports(data) {
        const id = generateRandomId();
        sendSocketMessage("copilot create-table-reports", { ...data, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(60000 * 3)
            )
            .toPromise();
    }

    async runTableAgent(data) {
        const id = generateRandomId();
        sendSocketMessage("copilot run-table-agent", { ...data, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(60000)
            )
            .toPromise();
    }

    async createFormula(data) {
        const id = generateRandomId();
        sendSocketMessage("copilot create-formula", { ...data, id });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(30000) // 30 saniye timeout
            )
            .toPromise();
    }

    async generateVoiceResponse(customerPrompt) {
        const id = generateRandomId();
        sendSocketMessage("copilot run-voice-agent", {
            siteId: this.siteId,
            customerPrompt,
            id,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(30000) // 30 saniye timeout
            )
            .toPromise();
    }

    async generateJavaScriptCode(data) {
        const id = generateRandomId();
        sendSocketMessage("copilot generate-javascript", {
            ...data,
            id,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(60000) // 60 saniye timeout
            )
            .toPromise();
    }

    async generateChatbotJavaScriptCode(data) {
        const id = generateRandomId();
        sendSocketMessage("copilot generate-chatbot-javascript", {
            ...data,
            id,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
                timeout(60000) // 60 saniye timeout
            )
            .toPromise();
    }

    async suggestFilters(data) {
        const id = generateRandomId();
        sendSocketMessage("copilot suggest-filters", {
            siteId: this.siteId,
            ...data,
            id,
        });
        return socketMessageStream
            .pipe(
                first((message) => message.cmd === "copilot_response" && message.data.id === id),
                map((message) => message.data.result),
            )
            .toPromise();
    }
}

export default CoPilot;
